import { DateRangeFilterButton } from '@e3dc-react/shell/Elements/DatePicker'
import { Filter, FilterOption } from '@e3dc-react/shell/Elements/Filter'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, Divider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import uniqWith from 'lodash/uniqWith'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useActiveInstallation } from 'src/Hooks'
import { useInstallationPageHandler } from 'src/Hooks/use-installation-page-handler'
import { getLocalStorageValue, saveToLocalStorage } from 'src/Libs/flow/hooks'
import { ChargingAuthType } from 'src/Routes/EMobility/EMobilityModels'
import { SolarEnergyCard } from './Cards/SolarEnergyCard'
import { SolarRatioCard } from './Cards/SolarRatioCard'
import { TotalGridEnergyCard } from './Cards/TotalGridEnergyCard'
import { ChargingSessionModel } from './charging-session-models'
import { getSessionStatusTranslation } from './charging-session-parser'
import { ChargingSessionChartContainer } from './ChargingSessionChart/ChargingSessionChartContainer'
import { ChargingSessionsExportButton } from './ChargingSessionsExportButton'
import { ChargingSessionResponsiveTable } from './ChargingSessionsTable/ChargingSessionResponsiveTable'
import { ChargingSessionTable } from './ChargingSessionsTable/ChargingSessionTable'

type EmobilityDateRangeCache =
  | {
      /** ISO date string */
      startDateMs: number | undefined
      /** ISO date string */
      endDateMs: number | undefined
    }
  | 'removedByUser'

export type DateRangeValue = {
  startDate: Date | undefined
  endDate: Date | undefined
}

export type EMobilityState = {
  midFilters: boolean[]
  statusFilters: string[]
  rfidFilters: string[]
  wallboxIdFilters: string[]
  currentPage: number
  rowsPerPage: number
  sort: { name: keyof ChargingSessionModel; direction: 'asc' | 'desc' }
  hiddenGridWallboxIds: string[]
  hiddenSolarWallboxIds: string[]
}

type ColumnFilterAction =
  | { type: 'SET_MID_FILTER'; value: boolean[] }
  | { type: 'SET_STATUS_FILTER'; value: string[] }
  | { type: 'SET_RFID_FILTER'; value: string[] }
  | { type: 'SET_WALLBOX_IDS'; value: string[] }
  | { type: 'CLEAR_ALL' }
  | { type: 'SET_CURRENT_PAGE'; page: number }
  | { type: 'SET_ROWS_PER_PAGE'; payload: { rows: number; sessionsCount: number } }
  | { type: 'SET_TABLE_SORT'; name: keyof ChargingSessionModel }
  | { type: 'TOGGLE_HIDDEN_GRID_WALLBOX_ID'; wallboxId: string }
  | { type: 'TOGGLE_HIDDEN_SOLAR_WALLBOX_ID'; wallboxId: string }

/** Since we need to also take into consideration and keep the choice of the user we have to introduce a new state which
 * is, in this case, 'removedByUser'.
 * This extra state needs to be used so we could differenciate two cases:
 * 1. If there is no cached date range the returned value should be the full month difference;
 * 2. If the user chooses to clear all the filters or clear the date we need the extra state of 'removedByUser' so we will not
 * return the full month difference.
 */
type DateRangeType = DateRangeValue | 'removedByUser'

const ONE_DAY_MS = 1000 * 60 * 60 * 24

/** Returns a date range representing the last 31 days. */
const getDefaultDateRange = (): DateRangeValue => ({
  /* In order to obtain a full month difference we multiply ms of a day by 31 days. */
  startDate: new Date(Date.now() - ONE_DAY_MS * 31),
  endDate: new Date(),
})

const cacheToDateRange = (cachedDate: EmobilityDateRangeCache | null): DateRangeValue | undefined => {
  if (!cachedDate || cachedDate === 'removedByUser') return undefined

  const { startDateMs, endDateMs } = cachedDate

  return {
    startDate: startDateMs !== undefined ? new Date(startDateMs) : undefined,
    endDate: endDateMs !== undefined ? new Date(endDateMs) : undefined,
  }
}

const dateRangeToCache = (dateRange: DateRangeType | undefined): EmobilityDateRangeCache | undefined => {
  if (!dateRange) return undefined

  if (dateRange === 'removedByUser') return 'removedByUser'

  const { startDate, endDate } = dateRange

  return {
    startDateMs: startDate?.getTime(),
    endDateMs: endDate?.getTime(),
  }
}

const initialEMobilityState: EMobilityState = {
  midFilters: [],
  statusFilters: [],
  rfidFilters: [],
  wallboxIdFilters: [],
  currentPage: 0,
  rowsPerPage: 10,
  sort: { name: 'startAt', direction: 'desc' },
  hiddenGridWallboxIds: [],
  hiddenSolarWallboxIds: [],
}

function emobilityStateReducer(state: EMobilityState, action: ColumnFilterAction): EMobilityState {
  switch (action.type) {
    case 'SET_MID_FILTER':
      return {
        ...state,
        midFilters: action.value,
        currentPage: 0,
        hiddenGridWallboxIds: [],
        hiddenSolarWallboxIds: [],
      }
    case 'SET_STATUS_FILTER':
      return {
        ...state,
        statusFilters: action.value,
        currentPage: 0,
        hiddenGridWallboxIds: [],
        hiddenSolarWallboxIds: [],
      }
    case 'SET_RFID_FILTER':
      return {
        ...state,
        rfidFilters: action.value,
        currentPage: 0,
        hiddenGridWallboxIds: [],
        hiddenSolarWallboxIds: [],
      }
    case 'SET_WALLBOX_IDS':
      return {
        ...state,
        wallboxIdFilters: action.value,
        currentPage: 0,
        hiddenGridWallboxIds: [],
        hiddenSolarWallboxIds: [],
      }
    case 'CLEAR_ALL':
      return {
        ...state,
        midFilters: [],
        statusFilters: [],
        rfidFilters: [],
        wallboxIdFilters: [],
        hiddenGridWallboxIds: [],
        hiddenSolarWallboxIds: [],
      }
    case 'SET_CURRENT_PAGE':
      return {
        ...state,
        currentPage: action.page,
      }
    case 'SET_ROWS_PER_PAGE': {
      const { rows, sessionsCount } = action.payload
      const isOverLastPage = rows * state.currentPage >= sessionsCount
      const lastPage = Math.floor(sessionsCount / rows)
      return {
        ...state,
        rowsPerPage: rows,
        currentPage: isOverLastPage ? lastPage : state.currentPage,
      }
    }
    case 'SET_TABLE_SORT': {
      if (action.name === state.sort.name) {
        return {
          ...state,
          sort: {
            ...state.sort,
            direction: state.sort.direction === 'asc' ? 'desc' : 'asc',
          },
        }
      }

      return {
        ...state,
        sort: {
          name: action.name,
          direction: 'asc',
        },
      }
    }
    case 'TOGGLE_HIDDEN_GRID_WALLBOX_ID': {
      return {
        ...state,
        hiddenGridWallboxIds: state.hiddenGridWallboxIds.includes(action.wallboxId)
          ? state.hiddenGridWallboxIds.filter(id => id !== action.wallboxId)
          : [...state.hiddenGridWallboxIds, action.wallboxId],
      }
    }
    case 'TOGGLE_HIDDEN_SOLAR_WALLBOX_ID': {
      return {
        ...state,
        hiddenSolarWallboxIds: state.hiddenSolarWallboxIds.includes(action.wallboxId)
          ? state.hiddenSolarWallboxIds.filter(id => id !== action.wallboxId)
          : [...state.hiddenSolarWallboxIds, action.wallboxId],
      }
    }
    default:
      return state
  }
}

interface EMobilityChargingSessionsProps {
  installationId: number
  storageId: string
  chargingSessions: ChargingSessionModel[]
  displayEnergyAll: boolean
}

export const EMobilityChargingSessions: React.FC<EMobilityChargingSessionsProps> = ({
  installationId,
  storageId,
  chargingSessions,
  displayEnergyAll,
}) => {
  const { t } = useCustomTranslation()
  useInstallationPageHandler('emobility.header.title')

  const theme = useTheme()
  const rootRef = useRef<HTMLDivElement | null>(null)

  const downMd = useMediaQuery(theme.breakpoints.down('md'))

  const [dateRange, setDateRange] = useState<DateRangeValue | undefined>(() => {
    const cachedEmobilityDateRange = getLocalStorageValue<EmobilityDateRangeCache>(`emobility.${installationId}.date-range`)
    return cacheToDateRange(cachedEmobilityDateRange) ?? getDefaultDateRange()
  })

  const updateDateRange = (newDateRange: DateRangeType | undefined): void => {
    if (newDateRange === 'removedByUser') {
      setDateRange(undefined)
    } else {
      setDateRange(newDateRange)
    }

    const newDateRangeCache = dateRangeToCache(newDateRange)
    saveToLocalStorage(`emobility.${installationId}.date-range`, newDateRangeCache)
  }

  const cachedEMobilityState: EMobilityState | null = getLocalStorageValue<EMobilityState>(`emobility.${installationId}.state`)
  const [emobilityState, dispatch] = useReducer(emobilityStateReducer, {
    ...initialEMobilityState,
    ...cachedEMobilityState,
  })
  saveToLocalStorage(`emobility.${installationId}.state`, emobilityState)

  const { midFilters, statusFilters, rfidFilters, wallboxIdFilters } = emobilityState

  const midFilter = useCallback(
    (session: ChargingSessionModel) => {
      if (midFilters.length === 0) return true

      return midFilters.includes(session.isMidCertified)
    },
    [midFilters]
  )

  const statusFilter = useCallback(
    (session: ChargingSessionModel) => {
      if (statusFilters.length === 0) return true

      return statusFilters.includes(session.status)
    },
    [statusFilters]
  )

  const rfIdFiltersIncludeEmpty = emobilityState.rfidFilters.includes('')
  const rfidFilter = useCallback(
    (session: ChargingSessionModel) => {
      if (rfidFilters.length === 0) return true

      /* This supports selection of hardcoded "No authentication" option. All other options are populated
       * from the data
       */
      if (session.authentication.type === 'NONE' && rfIdFiltersIncludeEmpty) return true

      if (!session.authentication.data) return false

      return rfidFilters.includes(session.authentication.data)
    },
    [rfidFilters, rfIdFiltersIncludeEmpty]
  )

  const wallboxFilter = useCallback(
    (session: ChargingSessionModel) => {
      if (wallboxIdFilters.length === 0) return true

      return wallboxIdFilters.includes(session.wallboxID)
    },
    [wallboxIdFilters]
  )

  const dateRangeFilter = useCallback(
    (session: ChargingSessionModel) => {
      if (!dateRange) return true

      return moment
        .utc(session.startAt)
        .isBetween(moment(dateRange.startDate).startOf('day'), moment(dateRange.endDate).endOf('day'), undefined, '[]')
    },
    [dateRange]
  )

  const filters: ((session: ChargingSessionModel) => boolean)[] = useMemo(
    () => [midFilter, statusFilter, rfidFilter, wallboxFilter, dateRangeFilter],
    [midFilter, statusFilter, rfidFilter, wallboxFilter, dateRangeFilter]
  )

  const { filteredChargingSessions, totalEnergyKWh, totalSolarEnergyKWh } = useMemo(() => {
    const filteredChargingSessions: ChargingSessionModel[] = []
    let totalEnergyWh = 0
    let totalSolarEnergyWh = 0

    for (const session of chargingSessions ?? []) {
      const passesAllFilters = filters.every(filter => filter(session))
      if (!passesAllFilters) continue

      filteredChargingSessions.push(session)
      totalEnergyWh += session.energyAll || 0
      totalSolarEnergyWh += session.energySolar || 0
    }

    return {
      filteredChargingSessions,
      totalEnergyKWh: totalEnergyWh / 1000,
      totalSolarEnergyKWh: totalSolarEnergyWh / 1000,
    }
  }, [filters, chargingSessions])

  const totalGridEnergyKWh = totalEnergyKWh - totalSolarEnergyKWh

  const minDate = moment.min((chargingSessions || []).map(session => moment.utc(session.startAt)))?.toDate()
  const maxDate = moment().endOf('day').toDate()

  const someSessionIsMidCertified = chargingSessions.some(({ isMidCertified }) => isMidCertified)
  const midOptions: FilterOption<boolean>[] = [
    ...(someSessionIsMidCertified ? [{ label: t('emobility.filters.mid.activeOption'), value: true }] : []),
    { label: t('emobility.filters.mid.inactiveOption'), value: false },
  ]

  const statusOptions = uniqWith(chargingSessions, (sessionA, sessionB) => sessionA.status === sessionB.status).map(session => ({
    label: getSessionStatusTranslation(t, session.status),
    value: session.status,
  }))

  const sessionRfidOptions = uniqWith(
    chargingSessions?.filter(session => session.authentication.data),
    (sessionA, sessionB) => sessionA.authentication.data === sessionB.authentication.data
  ).map(session => ({
    label: session.authentication.cardName
      ? t('emobility.chargingSession.authenticationInfoCardName', {
          cardName: session.authentication.cardName,
          rfid: session.authentication.data,
          label: ChargingAuthType.RFID,
        })
      : t('emobility.chargingSession.authenticationInfoRfid', {
          rfid: session.authentication.data,
          label: ChargingAuthType.RFID,
        }),
    value: session.authentication.data || '',
  }))
  /**
   * Per design, we not only filter for RFIDs found in session data, but also for sessions started with no authentication.
   * If such sessions exist, we hardcode an additional "No authentication" option to the filter besides the "real" RFIDs
   */
  const includesUnauthenticatedSessions = chargingSessions?.some(session => session.authentication.type === 'NONE')
  const rfidOptions = [
    ...(includesUnauthenticatedSessions
      ? [
          {
            label: t('emobility.chargingSession.noAuthentication'),
            value: '',
          },
        ]
      : []),
    ...sessionRfidOptions,
  ]
  const wallboxOptions = uniqWith(chargingSessions, (sessionA, sessionB) => {
    return sessionA.wallboxID === sessionB.wallboxID
  }).map(session => ({
    value: session.wallboxID,
    label: session.deviceName,
  }))

  const setPage = (page: number): void => {
    dispatch({ type: 'SET_CURRENT_PAGE', page })
  }

  const setRowsPerPage = (rows: number, sessionsCount: number): void => {
    dispatch({ type: 'SET_ROWS_PER_PAGE', payload: { rows, sessionsCount } })
  }

  const setSort = (name: keyof ChargingSessionModel): void => {
    dispatch({ type: 'SET_TABLE_SORT', name })
  }

  const toggleHiddenWallbox = (wallboxType: 'grid' | 'solar', wallboxId: string): void => {
    if (wallboxType === 'grid') {
      dispatch({ type: 'TOGGLE_HIDDEN_GRID_WALLBOX_ID', wallboxId })
    } else {
      dispatch({ type: 'TOGGLE_HIDDEN_SOLAR_WALLBOX_ID', wallboxId })
    }
  }

  const hasColumnFilters =
    emobilityState.midFilters.length > 0 ||
    emobilityState.statusFilters.length > 0 ||
    emobilityState.rfidFilters.length > 0 ||
    emobilityState.wallboxIdFilters.length > 0 ||
    Boolean(dateRange)

  useActiveInstallation(true, installationId.toString(), storageId)

  /**
   * ContentView.tsx defines a global flex layout that allows its children to expand as much as needed.
   *
   * Session table component is scrollable but must have a width constraint from the parent component.
   * This can only be possible if ContentView.tsx's root element has a minWidth of 0.
   *
   * To set this property from inside the child, we apply a DOM manipulation to avoid potential side effects on other components.
   * This whole component will soon be removed from the codebase, so this hack is acceptable.
   */
  useEffect(() => {
    for (let el = rootRef.current?.parentElement; el; el = el.parentElement) {
      const { display, flexDirection } = globalThis.getComputedStyle(el)

      if (display.includes('flex') && flexDirection === 'column') {
        const contentViewContainer = el
        const previouslySetMinWidth = contentViewContainer.style.minWidth
        contentViewContainer.style.minWidth = '0' /** temporary override */

        return () => {
          contentViewContainer.style.minWidth = previouslySetMinWidth
        }
      }
    }
  }, [])

  return (
    <Box mb={4} ref={rootRef}>
      <Box overflow="auto" px={{ xs: 2, md: 0 }} width="100%">
        <Stack direction="row" columnGap={1.5} alignItems="center" sx={{ minWidth: '100%', width: 'max-content' }} pb={1.5}>
          <Filter
            label={t('emobility.filters.mid')}
            options={midOptions}
            value={emobilityState.midFilters}
            onChange={value => dispatch({ type: 'SET_MID_FILTER', value })}
          />
          <Filter
            label={t('emobility.chargingSession.status')}
            options={statusOptions}
            value={emobilityState.statusFilters}
            onChange={value => dispatch({ type: 'SET_STATUS_FILTER', value })}
          />
          <Filter
            label={t('emobility.chargingSession.rfid')}
            options={rfidOptions}
            value={emobilityState.rfidFilters}
            onChange={value => dispatch({ type: 'SET_RFID_FILTER', value })}
            disabled={rfidOptions.length === 0}
          />
          <Filter
            label={t('emobility.chargingSession.wallbox')}
            options={wallboxOptions}
            value={emobilityState.wallboxIdFilters}
            onChange={value => dispatch({ type: 'SET_WALLBOX_IDS', value })}
          />
          <DateRangeFilterButton
            title={t('common.date')}
            value={dateRange}
            minDate={minDate}
            maxDate={maxDate}
            onChange={dateRange => {
              if (dateRange === undefined) {
                updateDateRange('removedByUser')
                return
              }
              updateDateRange(dateRange)
            }}
          />

          {hasColumnFilters && (
            <Button
              variant="text"
              onClick={() => {
                dispatch({ type: 'CLEAR_ALL' })
                updateDateRange('removedByUser')
              }}
              color="inherit"
              sx={{ minWidth: 'unset' }}
            >
              <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                {t('filter.clearAllButtonText')}
              </Typography>
            </Button>
          )}

          <Box ml="auto" display="flex">
            <ChargingSessionsExportButton />
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ borderColor: 'divider' }} />

      <Box>
        <Typography variant="h1" pt={3} pl={{ xs: 1, md: 0 }} mt={2}>
          {t('emobility.chargingSession.statistics')}
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} pt={2} pb={1.5} gap={1.5} flexWrap="wrap">
          <TotalGridEnergyCard energyKWh={totalGridEnergyKWh} />
          <SolarEnergyCard energyKWh={totalSolarEnergyKWh} />
          <SolarRatioCard totalGridEnergyKWh={totalGridEnergyKWh} totalSolarEnergyKWh={totalSolarEnergyKWh} />
        </Stack>

        <ChargingSessionChartContainer
          displayEnergyAll={displayEnergyAll}
          chargingSessions={filteredChargingSessions}
          dateRange={dateRange}
          state={emobilityState}
          installationId={installationId}
          toggleHiddenWallbox={toggleHiddenWallbox}
        />

        <Typography variant="h1" pt={3} pb={2} px={{ xs: 1, md: 0 }} mt={2}>
          {t('emobility.chargingSession.chargingSessions')}
        </Typography>

        <Box>
          {downMd ? (
            <ChargingSessionResponsiveTable installationId={installationId} chargingSessions={filteredChargingSessions} />
          ) : (
            <ChargingSessionTable
              installationId={installationId}
              chargingSessions={filteredChargingSessions}
              page={emobilityState.currentPage}
              setPage={setPage}
              rowsPerPage={emobilityState.rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              sort={emobilityState.sort}
              setSort={setSort}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}
